import { ReactComponent as CrimeIconDark } from '@/../../public/crimeIconDark.svg';
import { ReactComponent as CrimeIconLight } from '@/../../public/crimeIconLight.svg';
import { ReactComponent as DeleteIcon } from '@/../../public/deleteIconRed.svg';
import { ReactComponent as DemoIconDark } from '@/../../public/demoIconDark.svg';
import { ReactComponent as DemoIconLight } from '@/../../public/demoIconLight.svg';
import { ReactComponent as ExpansionIconDark } from '@/../../public/expansionIconDark.svg';
import { ReactComponent as ExpansionIconLight } from '@/../../public/expansionIconLight.svg';
import { ReactComponent as AiAnalysisDark } from '@/../../public/flaskDark.svg';
import { ReactComponent as AiAnalysisLight } from '@/../../public/flaskLight.svg';
import { ReactComponent as HPIIconDark } from '@/../../public/hpiIconDark.svg';
import { ReactComponent as HPIIconLight } from '@/../../public/hpiIconLight.svg';
import { ReactComponent as IncomeIconDark } from '@/../../public/incomeIconDark.svg';
import { ReactComponent as IncomeIconLight } from '@/../../public/incomeIconLight.svg';
import { ReactComponent as MSAIconDark } from '@/../../public/msaIconDark.svg';
import { ReactComponent as MSAIconLight } from '@/../../public/msaIconLight.svg';
import { ReactComponent as NeighborhoodIconDark } from '@/../../public/neighborhoodIconDark.svg';
import { ReactComponent as NeighborhoodIconLight } from '@/../../public/neighborhoodIconLight.svg';
import { ReactComponent as RentIconDark } from '@/../../public/rentIconDark.svg';
import { ReactComponent as RentIconLight } from '@/../../public/rentIconLight.svg';
import { ReactComponent as SchoolsIconDark } from '@/../../public/schoolIconDark.svg';
import { ReactComponent as SchoolsIconLight } from '@/../../public/schoolIconLight.svg';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { Box, Button, IconButton, TextField, Typography } from '@mui/material';
import {
  Pencil,
  Save,
  MessageSquare,
  Undo2,
  ArrowUp,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
} from 'lucide-react';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { ReportTemplatesContext } from '..';
import '../template.module.css';
import AddBlockModal from './AddBlockModal';
import AddMapsToTemplateSection from './AddMapsToTemplateSection';
import BlockNameHeading from './BlockNameHeading';
import { NationalAverageCrimeIndex } from './CrimeBlocks';
import CustomSymbolizationModal from './CustomSymbolizationModal';
import DefaultBlock from './DefaultBlock';
import {
  DemographicGroupBlock,
  GradeBlock,
  NearestGroupsBlock,
  WhoWeAreBlock,
} from './DemographicsBlocks';
import {
  GenericAppreciationBlock,
  GenericBlock,
  GenericIncomeBlock,
} from './GenericBlocks';
import { TickMark } from './HelperSVGs';
import { HousePriceIndexGraphBlock, HousePriceIndexHeader } from './HpiBlocks';
import LiveMap from './LiveMap';
import { DiversificationBlock, StatsMSABlock, Top5Block } from './MsaBlocks';
import { NearestStoresBlock } from './NeighborhoodBlocks';
import {
  NearestThreeSchoolsBlock,
  SchoolDistrictNameBlock,
} from './SchoolsBlocks';
import McpSymbolization from './McpSymbolization';

function BlockSlotWrapper({
  gridPosition,
  activeIndices,
  sectionKey,
  swapBlocks: doSwap,
  children,
}) {
  const total = activeIndices.length;
  const canUp = gridPosition >= 3;
  const canDown = gridPosition + 3 < total;
  const canLeft = gridPosition % 3 !== 0;
  const canRight = gridPosition % 3 !== 2 && gridPosition + 1 < total;

  const handleArrow = neighborPos => {
    if (neighborPos < 0 || neighborPos >= total) return;
    doSwap(sectionKey, activeIndices[gridPosition], activeIndices[neighborPos]);
  };

  const arrowButtonSx = {
    color: '#39585C',
    padding: '0.25rem',
    minWidth: 28,
    width: 28,
    height: 28,
  };

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 0,
      }}
    >
      {/* Top: up arrow */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 28,
        }}
      >
        {canUp ? (
          <IconButton
            size="small"
            onClick={() => handleArrow(gridPosition - 3)}
            sx={arrowButtonSx}
            aria-label="Move up"
          >
            <ArrowUp size={18} />
          </IconButton>
        ) : null}
      </Box>

      {/* Middle: left arrow | content | right arrow */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'stretch',
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 28,
          }}
        >
          {canLeft ? (
            <IconButton
              size="small"
              onClick={() => handleArrow(gridPosition - 1)}
              sx={arrowButtonSx}
              aria-label="Move left"
            >
              <ArrowLeft size={18} />
            </IconButton>
          ) : null}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 28,
          }}
        >
          {canRight ? (
            <IconButton
              size="small"
              onClick={() => handleArrow(gridPosition + 1)}
              sx={arrowButtonSx}
              aria-label="Move right"
            >
              <ArrowRight size={18} />
            </IconButton>
          ) : null}
        </Box>
      </Box>

      {/* Bottom: down arrow */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 28,
        }}
      >
        {canDown ? (
          <IconButton
            size="small"
            onClick={() => handleArrow(gridPosition + 3)}
            sx={arrowButtonSx}
            aria-label="Move down"
          >
            <ArrowDown size={18} />
          </IconButton>
        ) : null}
      </Box>
    </Box>
  );
}

export default function CustomizeTemplateSection() {
  const {
    setCurrentStage,
    setLastStage,
    lastStage,
    currentStage,
    saveTemplate,
    Sections,
  } = useContext(ReportTemplatesContext);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '1.25rem',
        border: '1px solid #EDF2F7',
        borderRadius: '1rem',
        paddingX: '1.25rem',
        paddingY: '1.25rem',
        marginX: '1rem',
      }}
    >
      {/* Heading and Button */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Lato',
            fontSize: '1rem',
            fontWeight: 800,
            lineHeight: '24px',
            letterSpacing: '0.2px',
            textAlign: 'left',
            color: '#1A202C',
          }}
        >
          Select Data Blocks To Add To Your Maps
        </Typography>

        <Button
          variant='contained'
          sx={{
            width: 'auto',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '3.5rem',
            backgroundColor: '#4FA490',
            color: '#FFFFFF',
            padding: '0.5rem 1.25rem 0.5rem 1.25rem',
            borderRadius: '0.75rem',
            '&:hover': {
              backgroundColor: '#4FA490',
            },
          }}
          startIcon={<TickMark />}
          onClick={() => {
            if (currentStage === 'customizeTemplate') {
              const isBlockSelected = Sections.some(
                section =>
                  section.status &&
                  (section.key === 'rent' ||
                    section.blocks.some(block => block.status)),
              );

              if (!isBlockSelected) {
                alert('At least select one Data Block');
                return;
              }

              setCurrentStage('saveAndFinish');
              if (lastStage === 'customizeTemplate') {
                setLastStage('saveAndFinish');
              }
            } else if (currentStage === 'saveAndFinish') {
              saveTemplate();
            }
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Lato',
              fontSize: '1rem',
              fontWeight: 700,
              lineHeight: '24px',
              letterSpacing: '0.2px',
              textAlign: 'center',
            }}
          >
            {currentStage === 'customizeTemplate'
              ? 'Save Data Selection'
              : 'Save Report Template'}
          </Typography>
        </Button>
      </Box>

      {/* Report Section */}
      <ReportSection />
    </Box>
  );
}

function ReportSection() {
  const {
    updateSections,
    addBlockModal,
    Sections,
    fetchToken,
    editing,
    setSections,
    changeMapToDefault,
    handleSaveCustomMap,
  } = useContext(ReportTemplatesContext);

  const [activeSection, setActiveSection] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [token, setToken] = useState(null);
  const [symbolizationModal, setSymbolizationModal] = useState(() => {
    const initialState = {};
    Sections.forEach(section => {
      initialState[section.key] = false;
    });
    return initialState;
  });
  const [defaultMapFlag, setDefaultMapFlag] = useState(() => {
    const initialState = {};
    Sections.forEach(section => {
      initialState[section.key] =
        editing === true ? (section?.mapBlock ? false : true) : false;
    });
    return initialState;
  });
  const [customSymbolizationBlock, setCustomSymbolizationBlock] = useState(
    () => {
      const initialState = {};
      Sections.forEach(section => {
        initialState[section.key] =
          editing === true
            ? section?.mapBlock
              ? section.mapBlock
              : null
            : null;
      });
      return initialState;
    },
  );
  const [mcpSymbolization, setMcpSymbolization] = useState(false);

  // Sync customSymbolizationBlock when Sections change (e.g. after loadTemplate)
  useEffect(() => {
    if (!editing) return;
    setCustomSymbolizationBlock(prev => {
      const next = { ...prev };
      Sections.forEach(section => {
        if (section.mapBlock) {
          next[section.key] = section.mapBlock;
        }
      });
      return next;
    });
  }, [editing, Sections]);

  // Function to add a field as a block to the template
  const handleAddFieldToTemplate = (blockData, sectionKey) => {
    const updatedSections = Sections.map(section => {
      if (section.key === sectionKey) {
        const activeBlocksCount = section.blocks.filter(
          secBlock => secBlock.status,
        ).length;
        if (activeBlocksCount >= 9) {
          alert('Cannot add more than 9 blocks in a section');
          return section;
        }

        // Check if block already exists
        const existingBlockIndex = section.blocks.findIndex(
          b =>
            b.title === blockData.title &&
            b.serviceURL === blockData.serviceURL,
        );

        if (existingBlockIndex >= 0) {
          // Update existing block status
          section.blocks[existingBlockIndex] = {
            ...section.blocks[existingBlockIndex],
            status: true,
          };
        } else {
          // Add new block
          section.blocks.push({
            ...blockData,
            status: true,
          });
        }
      }
      return section;
    });

    setSections(updatedSections);
  };

  const sectionRefs = useRef([]);

  // Stable key per block so React preserves component state (value, prefix, suffix) when reordering
  function getBlockKey(block, sectionKey) {
    return `${sectionKey}-${block.title}-${block?.group ?? ''}-${block?.serviceURL ?? ''}`;
  }

  function renderBlock(block, index, sectionKey, blockTitle) {
    switch (block.title) {
      // Demographics
      case 'Nearest Groups':
        return <NearestGroupsBlock key={index} />;
      case 'Grade':
        return <GradeBlock key={index} />;
      case 'Demographic Group':
        return <DemographicGroupBlock key={index} />;
      case 'Who We Are':
        return <WhoWeAreBlock key={index} />;

      // Crime

      case 'Overall Crime Index Score': {
        return (
          <NationalAverageCrimeIndex
            title={block?.title}
            sectionKey={sectionKey}
          />
        );
      }

      case 'Property Crime Index Score': {
        return (
          <NationalAverageCrimeIndex
            title={block?.title}
            sectionKey={sectionKey}
          />
        );
      }

      // Expansion
      case 'Block':
        return (
          <GenericBlock
            sectionKey={sectionKey}
            key={index}
            title={block.title}
          />
        );
      case 'Tract':
        return (
          <GenericBlock
            sectionKey={sectionKey}
            key={index}
            title={block.title}
          />
        );
      case 'Zip':
        return (
          <GenericBlock
            sectionKey={sectionKey}
            key={index}
            title={block.title}
          />
        );
      case 'County':
        return (
          <GenericBlock
            sectionKey={sectionKey}
            key={index}
            title={block.title}
          />
        );
      case 'State':
        return (
          <GenericBlock
            sectionKey={sectionKey}
            key={index}
            title={block.title}
          />
        );
      case 'National':
        return (
          <GenericBlock
            sectionKey={sectionKey}
            key={index}
            title={block.title}
          />
        );

      // Rent
      case 'Average':
        return (
          <GenericBlock
            sectionKey={sectionKey}
            key={index}
            title={block.title}
          />
        );
      case 'Median':
        return (
          <GenericBlock
            sectionKey={sectionKey}
            key={index}
            title={block.title}
          />
        );
      case '25 ~ Percentile':
        return (
          <GenericBlock
            sectionKey={sectionKey}
            key={index}
            title={block.title}
          />
        );
      case '75 ~ Percentile':
        return (
          <GenericBlock
            sectionKey={sectionKey}
            key={index}
            title={block.title}
          />
        );

      // Neighborhood:
      case 'Nearest Stores':
        return <NearestStoresBlock />;

      // HPI:
      case 'Price Appreciation Since 2011':
        return (
          <GenericAppreciationBlock title={'Price Appreciation Since 2011'} />
        );

      case 'Background Rate of Appreciation (1990 - present)':
        return (
          <GenericAppreciationBlock
            title={'Background Rate of Appreciation (1990 - present)'}
          />
        );

      case 'HPI TRACT/ZIP INDEX':
        return <HousePriceIndexGraphBlock />;

      // Schools
      case 'School District Name':
        return <SchoolDistrictNameBlock />;

      case 'Nearest 3 Schools':
        return <NearestThreeSchoolsBlock />;

      // Income
      case 'Median Household Income':
        return (
          <GenericIncomeBlock
            title='Median Household Income'
            sectionKey='income'
            blocks={[
              ['Block', 'Tract', 'Zip'],
              ['County', 'State', 'National'],
            ]}
          />
        );

      case 'Median Home Value':
        return (
          <GenericIncomeBlock
            title='Median Home Value'
            sectionKey='income'
            blocks={[['Block', 'Tract', 'Zip']]}
          />
        );

      case 'Annual Forecasted Median Income Growth':
        return (
          <GenericIncomeBlock
            title='Annual Forecasted Median Income Growth'
            sectionKey='annual_income'
            blocks={[['Tract', 'Zip']]}
          />
        );

      // MSA
      case 'Stat Block':
        return <StatsMSABlock />;

      case 'Business Diversification Index':
        return (
          <DiversificationBlock title={'Business Diversification Index'} />
        );

      case 'Employers Diversification Index':
        return (
          <DiversificationBlock title={'Employers Diversification Index'} />
        );
      case 'Top 5 Businesses by Sector':
        return <Top5Block title={'Top 5 Businesses by Sector'} />;

      case 'Top 5 Employers by Sector':
        return <Top5Block title={'Top 5 Employers by Sector'} />;

      default:
        return (
          <DefaultBlock
            sectionKey={sectionKey}
            block={block}
            index={index}
            id={blockTitle}
          />
        );
    }
  }

  useEffect(() => {
    const observerOptions = {
      root: null, // null makes it observe the entire viewport
      rootMargin: '0px',
      threshold: [0.5, 1], // 50% of the section must be visible
    };

    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.getAttribute('id'));
        }
      });
    }, observerOptions);

    sectionRefs.current.forEach(ref => {
      if (ref) {
        observer.observe(ref);
      }
    });

    return () => {
      sectionRefs.current.forEach(ref => {
        if (ref) {
          observer.unobserve(ref);
        }
      });
    };
  }, []);

  useEffect(() => {
    const getToken = async () => {
      const fetchedToken = await fetchToken();
      setToken(fetchedToken);
    };

    getToken();
  }, []);

  const iconStyle = {
    height: '2.25rem',
    width: '2.25rem',
  };

  const swapBlocks = useCallback(
    (sectionKey, indexA, indexB) => {
      if (indexA === indexB) return;
      setSections(prev =>
        prev.map(section => {
          if (section.key !== sectionKey) return section;
          const blocks = section.blocks;
          if (
            indexA < 0 ||
            indexA >= blocks.length ||
            indexB < 0 ||
            indexB >= blocks.length
          ) {
            return section;
          }
          const updatedBlocks = [...blocks];
          const tmp = updatedBlocks[indexA];
          updatedBlocks[indexA] = updatedBlocks[indexB];
          updatedBlocks[indexB] = tmp;
          return { ...section, blocks: updatedBlocks };
        }),
      );
    },
    [],
  );

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1.25rem',
        }}
      >
        {/* Add Block Modal */}
        {addBlockModal?.status && <AddBlockModal />}

        {Sections.filter(section => section.status === true).map(
          (section, index) =>
            section.status && (
              <Box
                key={index}
                ref={el => (sectionRefs.current[index] = el)} // Assign the ref to the section
                id={section.key}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1rem',
                }}
              >
                <ReportSectionHeader
                  icon={
                    <>
                      {
                        {
                          demographics: <DemoIconDark style={iconStyle} />,

                          income: <IncomeIconDark style={iconStyle} />,

                          hpi: <HPIIconDark style={iconStyle} />,
                          schools: <SchoolsIconDark style={iconStyle} />,
                          crime: <CrimeIconDark style={iconStyle} />,
                          expansion: <ExpansionIconDark style={iconStyle} />,
                          rent: <RentIconDark style={iconStyle} />,
                          neighborhood: (
                            <NeighborhoodIconDark style={iconStyle} />
                          ),
                          msa: <MSAIconDark style={iconStyle} />,
                          ai_analysis: <AiAnalysisDark style={iconStyle} />,
                        }[section.key]
                      }
                    </>
                  }
                  title={section.title}
                  sectionKey={section.key}
                />

                <InfoLabel
                  info={
                    'Default Map is used in case of no custom symbolization*'
                  }
                />
                {section.map.status}
                {section.status}
                {defaultMapFlag[section.key]}
                {(section.map.status === true || section.status === true) &&
                  section.map.webmapID &&
                  token &&
                  (defaultMapFlag[section.key] === true ? (
                    // DEFAULT MAP VIEW
                    <>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Button
                          sx={{
                            backgroundColor: '#F9F9F9',
                            color: '#004852',
                            border: '2px solid #004852',
                            height: '2.75rem',
                            width: '8rem',
                            padding: '0.5rem 1rem',
                            borderRadius: '0.75rem',
                            marginRight: '1rem',
                            '&:hover': {
                              backgroundColor: '#F0F0F0',
                            },
                          }}
                          startIcon={<Undo2 />}
                          onClick={() => {
                            setDefaultMapFlag(prev => {
                              return { ...prev, [section.key]: false };
                            });
                            setCustomSymbolizationBlock(prev => {
                              return { ...prev, [section.key]: null };
                            });
                            setSymbolizationModal(prev => {
                              return { ...prev, [section.key]: true };
                            });
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: 'Lato',
                              fontSize: '0.9rem',
                              fontWeight: 700,
                            }}
                          >
                            Change
                          </Typography>
                        </Button>

                        <Button
                          sx={{
                            backgroundColor: '#F9F9F9',
                            color: '#004852',
                            border: '2px solid #004852',
                            height: '2.75rem',
                            width: 'fit-content',
                            padding: '0.5rem 1rem',
                            borderRadius: '0.75rem',
                            marginRight: '1rem',
                            '&:hover': {
                              backgroundColor: '#F0F0F0',
                            },
                          }}
                          startIcon={<MessageSquare />}
                          onClick={() => {
                            if (!customSymbolizationBlock[section.key]) {
                              alert(
                                'Please select a custom field first using the Set Symbolization Modal button',
                              );
                              return;
                            }
                            setMcpSymbolization(true);
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: 'Lato',
                              fontSize: '0.9rem',
                              fontWeight: 700,
                            }}
                          >
                            MCP Symbolization
                          </Typography>
                        </Button>
                      </Box>

                      {mcpSymbolization ? (
                        <McpSymbolization
                          webmapID={section?.map?.webmapID}
                          token={token}
                          defaultSettingsFlag={true}
                          setMcpSymbolization={setMcpSymbolization}
                          sectionKey={section.key}
                          setMapConfig={() => {
                            // something to do here
                          }}
                          onSaveCustomMap={handleSaveCustomMap}
                          onAddFieldToTemplate={handleAddFieldToTemplate}
                        />
                      ) : (
                        <LiveMap
                          webmapID={section?.map?.webmapID}
                          token={token}
                          defaultSettings={true}
                        />
                      )}
                    </>
                  ) : customSymbolizationBlock[section.key] ? (
                    <>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          marginBottom: '1rem',
                        }}
                      >
                        <Button
                          sx={{
                            backgroundColor: '#F9F9F9',
                            color: '#004852',
                            border: '2px solid #004852',
                            height: '2.75rem',
                            width: '8rem',
                            padding: '0.5rem 1rem',
                            borderRadius: '0.75rem',
                            marginRight: '1rem',
                            '&:hover': {
                              backgroundColor: '#F0F0F0',
                            },
                          }}
                          startIcon={<Undo2 />}
                          onClick={() => {
                            setDefaultMapFlag(prev => {
                              return { ...prev, [section.key]: false };
                            });
                            setCustomSymbolizationBlock(prev => {
                              return { ...prev, [section.key]: null };
                            });
                            setSymbolizationModal(prev => {
                              return { ...prev, [section.key]: true };
                            });
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: 'Lato',
                              fontSize: '0.9rem',
                              fontWeight: 700,
                            }}
                          >
                            Change
                          </Typography>
                        </Button>

                        <Button
                          sx={{
                            backgroundColor: '#F9F9F9',
                            color: '#004852',
                            border: '2px solid #004852',
                            height: '2.75rem',
                            width: 'fit-content',
                            padding: '0.5rem 1rem',
                            borderRadius: '0.75rem',
                            marginRight: '1rem',
                            '&:hover': {
                              backgroundColor: '#F0F0F0',
                            },
                          }}
                          startIcon={<MessageSquare />}
                          onClick={() => {
                            if (!customSymbolizationBlock[section.key]) {
                              alert(
                                'Please select a custom field first using the Set Symbolization Modal button',
                              );
                              return;
                            }
                            setMcpSymbolization(true);
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: 'Lato',
                              fontSize: '0.9rem',
                              fontWeight: 700,
                            }}
                          >
                            MCP Symbolization
                          </Typography>
                        </Button>
                      </Box>
                      {(() => {
                        const blockState =
                          customSymbolizationBlock[section.key];
                        // Check if this block has MCP symbolization config (has nested customMapConfig)
                        const hasMcpConfig =
                          blockState?.customMapConfig?.selectedBlock ||
                          blockState?.customMapConfig;
                        // Show MCP Symbolization if explicitly requested OR if block has MCP config
                        const shouldShowMcp = mcpSymbolization || hasMcpConfig;

                        return shouldShowMcp ? (
                          <McpSymbolization
                            webmapID={section?.map?.webmapID}
                            token={token}
                            defaultSettingsFlag={false}
                            sectionKey={section.key}
                            onSaveCustomMap={handleSaveCustomMap}
                            setMcpSymbolization={setMcpSymbolization}
                            setMapConfig={() => {
                              // pass for now
                            }}
                            onAddFieldToTemplate={handleAddFieldToTemplate}
                            customMapConfig={(() => {
                              const saved =
                                section.mapBlock?.customMapConfig;
                              if (saved) {
                                return {
                                  type: saved.type || 'custom',
                                  mapSettings: saved.mapSettings || {},
                                  legendSettings:
                                    saved.legendSettings || {},
                                  selectedBlock:
                                    saved.selectedBlock ||
                                    section.mapBlock,
                                };
                              }
                              if (blockState) {
                                const fromBlock =
                                  blockState.customMapConfig;
                                return {
                                  type: fromBlock?.type || 'custom',
                                  mapSettings:
                                    fromBlock?.mapSettings || {},
                                  legendSettings:
                                    fromBlock?.legendSettings || {},
                                  selectedBlock:
                                    fromBlock?.selectedBlock ||
                                    blockState,
                                };
                              }
                              return null;
                            })()}
                          />
                        ) : (
                          <LiveMap
                            webmapID={section?.map?.webmapID}
                            token={token}
                            defaultSettings={false}
                            selectedBlock={blockState}
                          />
                        );
                      })()}
                    </>
                  ) : (
                    <Box
                      sx={{
                        flex: 1,
                        backgroundColor: '#F9F9F9',
                        paddingY: '1.25rem',
                        paddingX: '1rem',
                        borderRadius: '1rem',
                        display: 'flex',
                        gap: '1rem',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: 'Lato',
                          lineHeight: '1.5rem',
                          textAlign: 'center',
                          fontSize: '1.1rem',
                          fontWeight: 700,
                          color: '#39585C',
                        }}
                      >
                        Custom Symbolization
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '1rem',
                          alignItems: 'center',
                        }}
                      >
                        <Button
                          sx={{
                            boxSizing: 'border-box',
                            backgroundColor: '#39585C',
                            color: '#FFFFFF',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            borderRadius: '1.2rem',
                            padding: '16px',
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: '#39585C',
                              color: '#FFFFFF',
                            },
                          }}
                          onClick={() => {
                            setSymbolizationModal(prev => {
                              return { ...prev, [section.key]: true };
                            });
                          }}
                        >
                          Set Symbolization Modal
                        </Button>
                      </Box>
                    </Box>
                  ))}

                {symbolizationModal[section.key] && (
                  <CustomSymbolizationModal
                    Section={section}
                    setModal={() => {
                      setSymbolizationModal(prev => {
                        return { ...prev, [section.key]: false };
                      });
                    }}
                    // here
                    setDefaultMapFlag={() => {
                      changeMapToDefault(section.title, section.key);
                      setDefaultMapFlag(prev => {
                        return { ...prev, [section.key]: true };
                      });
                    }}
                    defaultMapFlag={defaultMapFlag[section.key]}
                    setBlock={block => {
                      setCustomSymbolizationBlock(prev => {
                        return { ...prev, [section.key]: block };
                      });
                    }}
                  />
                )}

                {section.key !== 'rent' && (
                  <InfoLabel
                    info={'Up to 9 data blocks in 3 columns can be added*'}
                  />
                )}

                {section.blocks.some(block => block.status) && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      marginTop: '3rem',
                      marginBottom: '.5rem',
                    }}
                  >
                    <BlockNameHeading
                      title={
                        {
                          expansion: 'Annual Forecasted Population Growth',
                          crime: 'National Average Crime Index',
                          msa: 'Metropolitan Statistical Area',
                          rent: '999 9th St Nw, Washington, DC 20001',
                        }[section.key]
                      }
                    />
                  </Box>
                )}

                {
                  // FOR HPI HEADER
                  section.key === 'hpi' &&
                    section.blocks.some(
                      block =>
                        block.status &&
                        (block.title === 'Price Appreciation Since 2011' ||
                          block.title ===
                            'Background Rate of Appreciation (1990 - present)'),
                    ) && <HousePriceIndexHeader />
                }

                {section.key !== 'rent' &&
                  (() => {
                    const activeIndices = section.blocks
                      .map((_, i) => i)
                      .filter(i => section.blocks[i].status);
                    return (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '.75rem',
                        }}
                      >
                        {/* Row 1 */}
                        <Box
                          sx={{
                            display: 'flex',
                            gap: '.5rem',
                            flexWrap: 'wrap',
                          }}
                        >
                          {section.blocks
                            .filter(block => block.status === true)
                            .slice(0, 3)
                            .map((block, index) => {
                              const originalIndex =
                                section.blocks.findIndex(
                                  b =>
                                    b.title === block.title &&
                                    (b?.group ?? null) ===
                                      (block?.group ?? null),
                                );
                              const gridPosition = index;
                              return (
                                <BlockSlotWrapper
                                  key={getBlockKey(block, section.key)}
                                  gridPosition={gridPosition}
                                  activeIndices={activeIndices}
                                  sectionKey={section.key}
                                  swapBlocks={swapBlocks}
                                >
                                  {renderBlock(
                                    block,
                                    originalIndex,
                                    section.key,
                                    block.title,
                                  )}
                                </BlockSlotWrapper>
                              );
                            })}
                          {section.blocks.filter(
                            block => block.status === true,
                          ).length < 3 && (
                            <AddDataBlock sectionKey={section.key} />
                          )}
                        </Box>

                        {/* Row 2 */}
                        <Box
                          sx={{
                            display: 'flex',
                            gap: '.5rem',
                            flexWrap: 'wrap',
                          }}
                        >
                          {section.blocks
                            .filter(block => block.status === true)
                            .slice(3, 6)
                            .map((block, index) => {
                              const originalIndex =
                                section.blocks.findIndex(
                                  b =>
                                    b.title === block.title &&
                                    (b?.group ?? null) ===
                                      (block?.group ?? null),
                                );
                              const gridPosition = 3 + index;
                              return (
                                <BlockSlotWrapper
                                  key={getBlockKey(block, section.key)}
                                  gridPosition={gridPosition}
                                  activeIndices={activeIndices}
                                  sectionKey={section.key}
                                  swapBlocks={swapBlocks}
                                >
                                  {renderBlock(
                                    block,
                                    originalIndex,
                                    section.key,
                                    block.title,
                                  )}
                                </BlockSlotWrapper>
                              );
                            })}
                          {section.blocks.filter(
                            block => block.status === true,
                          ).length >= 3 &&
                            section.blocks.filter(
                              block => block.status === true,
                            ).length < 6 && (
                              <AddDataBlock sectionKey={section.key} />
                            )}
                        </Box>

                        {/* Row 3 */}
                        <Box
                          sx={{
                            display: 'flex',
                            gap: '.5rem',
                            flexWrap: 'wrap',
                          }}
                        >
                          {section.blocks
                            .filter(block => block.status === true)
                            .slice(6, 9)
                            .map((block, index) => {
                              const originalIndex =
                                section.blocks.findIndex(
                                  b =>
                                    b.title === block.title &&
                                    (b?.group ?? null) ===
                                      (block?.group ?? null),
                                );
                              const gridPosition = 6 + index;
                              return (
                                <BlockSlotWrapper
                                  key={getBlockKey(block, section.key)}
                                  gridPosition={gridPosition}
                                  activeIndices={activeIndices}
                                  sectionKey={section.key}
                                  swapBlocks={swapBlocks}
                                >
                                  {renderBlock(
                                    block,
                                    originalIndex,
                                    section.key,
                                    block.title,
                                  )}
                                </BlockSlotWrapper>
                              );
                            })}
                          {section.blocks.filter(
                            block => block.status === true,
                          ).length >= 6 &&
                            section.blocks.filter(
                              block => block.status === true,
                            ).length < 9 && (
                              <AddDataBlock sectionKey={section.key} />
                            )}
                        </Box>
                      </Box>
                    );
                  })()}
              </Box>
            ),
        )}

        <Box
          sx={{
            flex: 1,
            backgroundColor: '#F9F9F9',
            paddingY: '1.25rem',
            paddingX: '1rem',
            borderRadius: '1rem',
            display: 'flex',
            gap: '1rem',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Button
            sx={{
              boxSizing: 'border-box',

              backgroundColor: '#39585C',
              color: '#FFFFFF',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              borderRadius: '1.2rem',
              padding: '16px',

              cursor: 'pointer',

              '&:hover': {
                backgroundColor: '#39585C',
                color: '#FFFFFF',
              },
            }}
            onClick={() => setIsModalOpen(true)}
          >
            Add New Section
          </Button>
        </Box>

        <AddMapsToTemplateSection
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />
      </Box>
    </>
  );
}

function AddDataBlock({ sectionKey }) {
  const { setAddBlockModal, currentStage } = useContext(ReportTemplatesContext);
  if (currentStage !== 'customizeTemplate') return null;
  return (
    <Box
      sx={{
        flex: 1,
        backgroundColor: '#F9F9F9',
        borderRadius: '1rem',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        minHeight: '13rem',
        alignItems: 'center',
        gap: '0.5rem',
        cursor: 'pointer',
      }}
      onClick={() => {
        setAddBlockModal({
          status: true,
          callingSection: sectionKey,
        });
      }}
    >
      <Typography>Add Data Block</Typography>

      <AddCircleOutlineIcon
        sx={{
          color: '#4FA490',
        }}
      />
    </Box>
  );
}

function ReportSectionHeader({ icon, title, sectionKey }) {
  const { currentStage, Sections, setSections } = useContext(
    ReportTemplatesContext,
  );
  const [isEditing, setIsEditing] = useState(false);
  const [editableTitle, setEditableTitle] = useState(null);

  const handleTitleClick = () => {
    if (currentStage === 'customizeTemplate') {
      setIsEditing(true);
    }
  };

  const handleInputChange = event => {
    setEditableTitle(event.target.value);
  };

  const handleInputBlur = () => {
    setIsEditing(false);
    // Update the section title in the context
    const updatedSections = Sections.map(section => {
      if (section.key === sectionKey) {
        section.title = editableTitle;
      }
      return section;
    });
    setSections(updatedSections);
  };

  useEffect(() => {
    setEditableTitle(title);
  }, [title]);

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingRight: '1rem',
      }}
    >
      {/* Icon and Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
        }}
      >
        {icon}
        {isEditing ? (
          <>
            <TextField
              value={editableTitle}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              autoFocus
              variant='standard'
              sx={{
                fontFamily: 'Lato',
                fontSize: '1rem',
                fontWeight: 700,
                lineHeight: '24px',
                letterSpacing: '0.15px',
                textAlign: 'left',
              }}
            />
            <Save
              height={18}
              width={18}
              color='#39585C'
              style={{
                cursor:
                  currentStage === 'customizeTemplate' ? 'pointer' : 'default',
              }}
              onClick={handleInputBlur}
            />
          </>
        ) : (
          <>
            <Typography
              sx={{
                fontFamily: 'Lato',
                fontSize: '1rem',
                fontWeight: 700,
                lineHeight: '24px',
                letterSpacing: '0.15px',
                textAlign: 'left',
              }}
            >
              {editableTitle}
            </Typography>

            <Pencil
              height={18}
              width={18}
              color='#39585C'
              onClick={handleTitleClick}
              style={{
                cursor:
                  currentStage === 'customizeTemplate' ? 'pointer' : 'default',
              }}
            />
          </>
        )}
      </Box>

      {/* Delete Button */}
      {currentStage === 'customizeTemplate' && (
        <Box
          sx={{
            border: '2px solid #D32F2F',
            borderRadius: '0.75rem',
            width: '3.3rem',
            height: '3rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
          onClick={() => {
            const updatedSections = Sections.map(section => {
              if (section.key === sectionKey) {
                section.status = false;
                section.map.status = false;
              }
              return section;
            });
            setSections(updatedSections);
          }}
        >
          <DeleteIcon height={25} width={25} />
        </Box>
      )}
    </Box>
  );
}

function InfoLabel({ info }) {
  return (
    <Typography
      sx={{
        fontFamily: 'Lato',
        fontSize: '1rem',
        fontWeight: 800,
        lineHeight: '20px',
        textAlign: 'left',
        color: '#9A9A9A',
        padding: '0.5rem',
        borderRadius: '0.75rem',
      }}
    >
      {info}
    </Typography>
  );
}
